import type {
  PlansTabFeatureValues,
  PlansTabFormValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';

export type SerializedFeature = {
  membershipFeatureId: string;
  name: string;
  sortOrder: number;
  defaultContent: string;
  defaultTooltip: string;
};

export type SerializedActiveFeature = {
  membershipFeatureId: string;
  overrideContent?: string;
  overrideTooltip?: string;
};

export type SerializedFeatureProduct = {
  id: string;
  name: string;
  activeFeatures: SerializedActiveFeature[];
};

export type SerializedFeaturesConfig = {
  features: SerializedFeature[];
  products: SerializedFeatureProduct[];
};

export type SerializedSavePayloadFeaturePlan = {
  planId: string;
  included: boolean;
  overrideContent: string | null;
  overrideTooltip: string | null;
};

export type SerializedSavePayloadFeature = {
  membershipFeatureId: string;
  name: string;
  sortOrder: number;
  defaultContent: string;
  defaultTooltip: string | null;
  plans: SerializedSavePayloadFeaturePlan[];
};

const normalizeOptionalText = (value?: string): string | undefined => {
  const normalized = value?.trim() ?? '';
  return normalized ? normalized : undefined;
};

const normalizeNullableText = (value?: string): string | null => {
  const normalized = value?.trim() ?? '';
  return normalized ? normalized : null;
};

const serializeFeature = (
  feature: PlansTabFeatureValues,
  sortOrder: number,
): SerializedFeature => ({
  membershipFeatureId: feature.id,
  name: feature.name,
  sortOrder,
  defaultContent: feature.defaultContent,
  defaultTooltip: feature.defaultTooltip ?? '',
});

export const serializeFeaturesConfig = (
  formValues: PlansTabFormValues,
): SerializedFeaturesConfig => {
  const { features, plans } = formValues;

  const sortedFeatures = [...features].sort((a, b) => a.sortOrder - b.sortOrder);

  const serializedFeatures = sortedFeatures.map((feature, index) =>
    serializeFeature(feature, index + 1),
  );

  const enabledPlansSorted = [...plans]
    .filter((plan) => plan.enabled)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const products: SerializedFeatureProduct[] = enabledPlansSorted.map((plan) => {
    const activeFeatures: SerializedActiveFeature[] = [];

    sortedFeatures.forEach((feature) => {
      if (!feature.includedPlanIds.includes(plan.id)) {
        return;
      }

      activeFeatures.push({
        membershipFeatureId: feature.id,
        overrideContent: normalizeOptionalText(feature.overrideContentByPlanId[plan.id]),
        overrideTooltip: normalizeOptionalText(feature.overrideTooltipByPlanId[plan.id]),
      });
    });

    return {
      id: plan.id,
      name: plan.name,
      activeFeatures,
    };
  });

  return {
    features: serializedFeatures,
    products,
  };
};

export const serializeFeaturesForPayload = (
  formValues: PlansTabFormValues,
): SerializedSavePayloadFeature[] => {
  const { features, plans } = formValues;

  const sortedFeatures = [...features].sort((a, b) => a.sortOrder - b.sortOrder);

  const enabledPlansSorted = [...plans]
    .filter((plan) => plan.enabled)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return sortedFeatures.map((feature, index) => {
    const includedPlanIds = new Set(feature.includedPlanIds ?? []);

    return {
      membershipFeatureId: feature.id,
      name: feature.name,
      sortOrder: index + 1,
      defaultContent: feature.defaultContent,
      defaultTooltip: normalizeNullableText(feature.defaultTooltip),
      plans: enabledPlansSorted.map((plan) => {
        const included = includedPlanIds.has(plan.id);

        return {
          planId: plan.id,
          included,
          overrideContent: normalizeNullableText(feature.overrideContentByPlanId[plan.id]),
          overrideTooltip: normalizeNullableText(feature.overrideTooltipByPlanId[plan.id]),
        };
      }),
    };
  });
};
